import React, { FC, useState, useEffect } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Button, Divider, Dropdown, Grid, Header, Message, Pagination } from 'semantic-ui-react'
import { Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import { useDatasetsCreator } from '../../contexts/DatasetsCreatorContext'
import CustomLabel from '../../components/common/custom-label/CustomLabel'
import Section from '../../components/common/Section'
import SubSection from '../../components/common/SubSection'
import Title from '../../components/common/Title'
import Data from '../../api/Data'
import Auth from '../../utils/Auth'
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown'
import LabelIcon from '@mui/icons-material/Label'
import './PreviewDataset.scss'

enum DataErrorTypes {
  DUPLICATE_HEADERS = 'duplicateHeaders',
  NO_KEY_COLUMN_CANDIDATES = 'noKeyColumnCandidates',
  ILLEGAL_CHARACTERS = 'illegalCharacters',
}

const PreviewDataset: FC<IProps> = ({ history }) => {
  const {
    csvData,
    formData = {
      title: '',
      description: '',
      hasHeader: true,
      dataType: 'row',
    },
    keyColumn,
    selectedColumns = [],
    setKeyColumn,
    setSelectedColumns,
    uploadCSV,
    transposeData,
  } = useDatasetsCreator()

  const getInverseDataType = () => (formData.dataType === 'row' ? 'column' : 'row')

  if (!setKeyColumn || !setSelectedColumns || !uploadCSV || !transposeData) {
    throw new Error('DatasetsCreator context was not initialized properly')
  }

  const [processedData, setProcessedData] = useState<string[][]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [uniqueColumns, setUniqueColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [hasDataError, setHasDataError] = useState<DataErrorTypes>()
  const [countdown, setCountdown] = useState<number | null>(null)
  const [columnsTagState, setColumnsTagState] = useState<{ [key: string]: boolean }>({})
  const [illegalColumns, setIllegalColumns] = useState<string[]>([])

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const rowsPerPage = 20

  // Validate if we have CSV data and form data
  useEffect(() => {
    setHasDataError(undefined)

    if (!csvData?.length) {
      setError(
        'Something went wrong - we can\'t seem to find your data. Please try and create the dataset again from the "My Datasets" page we\'ll automatically redirect you there soon.'
      )
      setCountdown(5)
    } else {
      let columnsList
      let newProcessedData = [...csvData]

      if (formData.hasHeader) {
        columnsList = csvData[0]

        if (checkDuplicateHeaders(columnsList)) {
          setHasDataError(DataErrorTypes.DUPLICATE_HEADERS)
          return
        }

        const illegalCols = checkIllegalCharacters(columnsList)
        if (illegalCols.length > 0) {
          setIllegalColumns(illegalCols)
          setHasDataError(DataErrorTypes.ILLEGAL_CHARACTERS)
        }
      } else {
        columnsList = csvData[0].map((_: any, index: number) => `col_${index + 1}`)
        newProcessedData = [columnsList, ...csvData]
      }

      setColumns(columnsList)

      if (!selectedColumns?.length) {
        setSelectedColumns(columnsList)
      }

      setProcessedData(newProcessedData)
      const transposedData = transposeData(newProcessedData, 1)
      const uniqueColumnNames = transposedData
        .map((values: string[], index: number) => (new Set(values).size === values.length ? newProcessedData[0][index] : null))
        .filter((columnName: string | null): columnName is string => !!columnName)

      if (!uniqueColumnNames.length) {
        setHasDataError(DataErrorTypes.NO_KEY_COLUMN_CANDIDATES)
        return
      }

      setUniqueColumns(uniqueColumnNames)

      const dataRowCount = newProcessedData.length - 1
      setTotalPages(Math.ceil(dataRowCount / rowsPerPage))
    }
  }, [csvData, formData.hasHeader, selectedColumns, setSelectedColumns, transposeData])

  // Countdown for redirect
  useEffect(() => {
    if (!!countdown) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0) {
      history.push('/datasets')
    }
  }, [countdown, history])

  const checkDuplicateHeaders = (headers: string[]): boolean => {
    const headerCounts: Record<string, number> = {}

    headers.forEach((header) => {
      headerCounts[header] = (headerCounts[header] || 0) + 1
    })

    // Find duplicates
    const duplicates = Object.entries(headerCounts).filter(([_, count]) => count > 1)

    return duplicates.length > 0
  }

  const checkIllegalCharacters = (headers: string[]): string[] => {
    const illegalChars = ['.', '{', '}']
    const illegalColumns: string[] = []

    headers.forEach((header) => {
      if (illegalChars.some((char) => header.includes(char))) {
        illegalColumns.push(header)
      }
    })

    return illegalColumns
  }

  const hasSelectedIllegalColumns = (): boolean => {
    if (!illegalColumns.length) return false
    return selectedColumns.some((col) => illegalColumns.includes(col))
  }

  const getSelectedIllegalColumns = (): string[] => {
    return illegalColumns.filter((col) => selectedColumns.includes(col))
  }

  const getIllegalColumnsErrorMessage = (): string => {
    const selectedIllegalColumns = getSelectedIllegalColumns()
    if (selectedIllegalColumns.length === 1) {
      return `the column '${selectedIllegalColumns[0]}' contains unsupported characters`
    }
    return `the columns ${selectedIllegalColumns.map((c) => `'${c}'`).join(', ')} contain unsupported characters`
  }

  // Back button handler
  const handleBack = () => {
    uploadCSV(null)
    history.push('/create-dataset')
  }

  // Save dataset
  const handleSave = async () => {
    if (!keyColumn || selectedColumns.length === 0 || !!hasDataError || hasSelectedIllegalColumns()) return

    const { teamId } = Auth.getPayLoad() || {}
    if (!teamId) {
      setError('Team ID not found')
      return
    }

    try {
      setLoading(true)

      const columns = selectedColumns.map((colName: any) => {
        const colIndex = processedData[0].findIndex((header: string) => header === colName)

        if (colIndex === -1) throw new Error(`Column ${colName} not found in the data`)

        const values = processedData.slice(1).map((row: string[]) => row[colIndex])
        const isKey = colName === keyColumn
        const isConvertToTag = columnsTagState[colName] || false

        return {
          name: colName,
          isKey,
          isConvertToTag,
          values,
        }
      })

      // Prepare data for API in the format expected by the backend
      const dataToSave = {
        title: formData.title,
        description: formData.description || null,
        columns,
      }

      const { error } = await Data.createTeamDataset(teamId, dataToSave)

      if (!error) {
        setLoading(false)
        history.push('/datasets')
        return
      }

      if (error.response && error.response.data) {
        const errorMessage = error.response.data

        if (errorMessage.includes('not authorized') || errorMessage.includes('Team not found')) {
          setError('Something went wrong with your session. Please logout and back in before trying again.')
        } else if (
          errorMessage.includes('not well-formed') ||
          errorMessage.includes('Data for the dataset is missing')
        ) {
          setError('Something went wrong transforming your data. Please contact support.')
        } else if (error.response.status === 413) {
          setError('Sorry, your dataset is too big. We currently only support up to 1MB.')
        } else {
          setError(`Error: ${errorMessage}`)
        }
      } else {
        setError(`An error occurred. Please try again later.`)
      }
      setLoading(false)
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred. Please try again.')
      console.error('Failed to save dataset:', err)
      setLoading(false)
    }
  }

  // Get paginated data for the table
  const getTableData = () => {
    if (!processedData?.length) return []

    const startIndex = 1 // First row is always header in processedData
    const paginationStartIndex = startIndex + (currentPage - 1) * rowsPerPage
    const paginationEndIndex = paginationStartIndex + rowsPerPage

    return processedData.slice(paginationStartIndex, paginationEndIndex)
  }

  // Handle pagination change
  const handlePageChange = (_: React.SyntheticEvent, data: any) => {
    setCurrentPage(data.activePage)
  }

  // Generate dropdown options
  const getColumnOptions = () => {
    return columns.map((col) => ({
      key: col,
      text: col,
      value: col,
    }))
  }

  // Generate key column options (only columns with unique values)
  const getKeyColumnOptions = () => {
    return uniqueColumns.map((col) => ({
      key: col,
      text: col,
      value: col,
    }))
  }

  // Handle key column change
  const handleKeyColumnChange = (_: React.SyntheticEvent, data: any) => {
    setKeyColumn(data.value)

    // Ensure key column is also in selected columns
    if (!selectedColumns.includes(data.value)) {
      setSelectedColumns([...selectedColumns, data.value])
    }
  }

  // Handle selected columns change
  const handleSelectedColumnsChange = (_: React.SyntheticEvent, data: any) => {
    // Ensure key column stays selected if it exists
    if (keyColumn && !data.value.includes(keyColumn)) {
      data.value.push(keyColumn)
    }

    setSelectedColumns(data.value)

    if (hasDataError === DataErrorTypes.ILLEGAL_CHARACTERS) {
      const hasIllegalSelected = data.value.some((col: string) => illegalColumns.includes(col))
      if (!hasIllegalSelected) {
        setHasDataError(undefined)
      }
    } else if (illegalColumns.length > 0) {
      const hasIllegalSelected = data.value.some((col: string) => illegalColumns.includes(col))
      if (hasIllegalSelected) {
        setHasDataError(DataErrorTypes.ILLEGAL_CHARACTERS)
      }
    }
  }

  const handleTagIconClick = (columnName: string) => {
    setColumnsTagState((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }))
  }

  if (error && countdown !== null) {
    return (
      <Section>
        <div>
          <Title title="My Datasets" />
          <SubSection>
            <Message negative style={{ textAlign: 'center' }}>
              <Message.Header>{error}</Message.Header>
              <p>Redirecting in {countdown} seconds...</p>
            </Message>
          </SubSection>
        </div>
      </Section>
    )
  }

  return (
    <Section>
      <>
        <Title title="My Datasets">
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button onClick={handleBack}>Back</Button>
            <Button
              primary
              disabled={!keyColumn || selectedColumns.length === 0 || !!hasDataError || hasSelectedIllegalColumns()}
              onClick={handleSave}
              loading={loading}
              style={{ backgroundColor: '#5c50e6' }}
            >
              Add
            </Button>
          </div>
        </Title>

        <SubSection hasGreyBackground>
          <>
            <Header as="h1" style={{ marginBottom: '2rem', textAlign: 'left', fontWeight: 'bold' }}>
              Preview and Configure Your Dataset
              <Header.Subheader>
                Review your uploaded data, select a unique key column, and choose which fields to include in your
                project. This step ensures contributor information is correctly matched and tagged during video
                submission.
              </Header.Subheader>
            </Header>

            <div className="ui segment">
              <Grid className="dataset-header">
                <Grid.Row>
                  <Grid.Column>
                    <Header as="h2" className="bold">
                      Preview
                      <Header.Subheader>
                        Below is the standardized version of your dataset we will save. Use the arrows to scroll through
                        the pages and verify that all your data looks correct. Once you’ve chosen your key column and
                        tags, click Add to finalise your dataset setup.
                      </Header.Subheader>
                    </Header>
                  </Grid.Column>
                </Grid.Row>

                <Divider fitted />

                <Grid.Row columns={2}>
                  <Grid.Column stretched>
                    <Grid.Row>
                      <label className="bold">Select Key Column</label>
                    </Grid.Row>
                    <Grid.Row stretched>
                      <p>
                        Choose the column that uniquely identifies each record—like an email or ID. We use this to match
                        submitted data to your dataset and will appear as the selectable value in your video submission
                        forms (e.g. checkbox or dropdown options).
                        <CustomLabel>
                          <CustomLabel.Popup popupContent="Each row must have a value in this column. If the column contains blank or duplicate entries, it may cause issues." />
                        </CustomLabel>
                      </p>
                    </Grid.Row>
                    <Grid.Row>
                      <Dropdown
                        placeholder="Select key column here"
                        fluid
                        selection
                        options={getKeyColumnOptions()}
                        value={keyColumn || ''}
                        onChange={handleKeyColumnChange}
                      />
                    </Grid.Row>
                  </Grid.Column>
                  <Grid.Column stretched>
                    <Grid.Row>
                      <label className="bold">Selected Columns</label>
                    </Grid.Row>
                    <Grid.Row stretched>
                      <p>
                        Choose which columns you want to include in your dataset. You can untick any columns that aren’t
                        needed—such as internal notes, timestamps, or unused metadata.
                        <br />
                        For clarity and performance, we recommend keeping it to six columns or fewer.
                      </p>
                    </Grid.Row>
                    <Grid.Row>
                      <MultiSelectDropdown
                        placeholder="Select columns here"
                        fluid
                        options={getColumnOptions()}
                        value={selectedColumns}
                        onChange={handleSelectedColumnsChange}
                      />
                    </Grid.Row>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column stretched style={{ flexGrow: 1 }}>
                    <label className="bold">Tag Your Contributors' Data (Optional)</label>
                    <p>
                      Click the tag icon above any column to convert that data into searchable tags.
                      <CustomLabel>
                        <CustomLabel.Popup popupContent="Use columns that contain simple, short values (one to two words) for tagging. Avoid long text or multi-topic fields." />
                      </CustomLabel>
                    </p>
                  </Grid.Column>
                  <Grid.Column stretched verticalAlign="bottom">
                    <Pagination
                      activePage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      size="mini"
                      firstItem={null}
                      lastItem={null}
                      prevItem={{ content: '<', icon: true }}
                      nextItem={{ content: '>', icon: true }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              {selectedColumns.length > 25 && (
                <Message info>
                  <Message.Header>Large Number of Columns Selected</Message.Header>
                  <p>
                    Your file contains {selectedColumns.length} columns, and you’ve chose the “each {formData.dataType}{' '}
                    per record” format. Are you sure your data is structured this way? If not, click Back to change your
                    data layout.
                  </p>
                </Message>
              )}

              {hasDataError === DataErrorTypes.DUPLICATE_HEADERS && (
                <Message info>
                  <Message.Header>Duplicate Headers Found</Message.Header>
                  <p>
                    Your CSV file contains duplicate column headers, and you’ve chose the “each {formData.dataType} per
                    record” format. Are you sure this is correct? You can check your file or click Back to change your
                    data layout.
                  </p>
                </Message>
              )}

              {hasDataError === DataErrorTypes.NO_KEY_COLUMN_CANDIDATES && (
                <Message info>
                  <Message.Header>No Suitable Key Column Found</Message.Header>
                  <p>
                    We couldn’t find a {getInverseDataType()} with unique values. This is usually because every{' '}
                    {getInverseDataType()} contains blank cells or duplicate entries. Please check your file and ensure
                    there’s one {getInverseDataType()} where each {formData.dataType} has a distinct value—like an email
                    or ID.
                    <br />
                    If needed, go back and adjust how your data is arranged. Then re-upload (back two steps).
                    <CustomLabel>
                      <CustomLabel.Popup popupContent="Open your CSV in Excel, Google Sheets, or another spreadsheet tool. Use “Find & Replace”, “Remove Duplicates” or filters to search for missing or repeated values, then clean the column before re-uploading." />
                    </CustomLabel>
                  </p>
                </Message>
              )}

              {hasDataError === DataErrorTypes.ILLEGAL_CHARACTERS && hasSelectedIllegalColumns() && (
                <Message negative>
                  <Message.Header>Illegal Characters Found in Column Names</Message.Header>
                  <p>
                    Sorry, {getIllegalColumnsErrorMessage()}. The ".", "{'{'}" and "{'}'}" characters are not allowed.
                    You can unselect the problematic column(s) below to continue, or update your file and try to upload
                    it again.
                  </p>
                </Message>
              )}

              {error && (
                <Message negative>
                  <Message.Header>{error}</Message.Header>
                </Message>
              )}

              <div style={{ overflowX: 'auto', width: '100%', marginBottom: '20px', marginTop: '20px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow className="dataset-detail__table-header">
                      {columns
                        .filter((col) => selectedColumns.includes(col))
                        .sort((a, b) => {
                          if (a === keyColumn) return -1
                          if (b === keyColumn) return 1
                          return 0
                        })
                        .map((col, index) => (
                          <TableCell key={index} className="dataset-detail__table-header-cell">
                            <div className="dataset-detail__column-header">
                              <span className="dataset-detail__column-name">{col}</span>
                              <Tooltip
                                title={
                                  columnsTagState[col] ? 'Exclude as tag when linked' : 'Include as tag when linked'
                                }
                                placement="top"
                                arrow
                              >
                                <span
                                  className={`dataset-detail__tag-icon ${
                                    columnsTagState[col] ? 'dataset-detail__tag-icon--active' : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleTagIconClick(col)
                                  }}
                                >
                                  <LabelIcon fontSize="large" />
                                </span>
                              </Tooltip>
                            </div>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getTableData().map((row: any, rowIndex: number) => {
                      const filteredCells = row
                        .map((cell: any, colIndex: number) => {
                          const colName = processedData[0][colIndex]
                          if (selectedColumns.includes(colName)) {
                            return { cell, colName }
                          }
                          return null
                        })
                        .filter(Boolean)

                      const sortedCells = filteredCells.sort((a: any, b: any) => {
                        if (a.colName === keyColumn) return -1
                        if (b.colName === keyColumn) return 1
                        return 0
                      })

                      return (
                        <TableRow key={rowIndex}>
                          {sortedCells.map((item: any, cellIndex: number) => (
                            <TableCell key={cellIndex} className="dataset-detail__table-cell">
                              {item.cell}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        </SubSection>
      </>
    </Section>
  )
}

interface IProps extends RouteComponentProps {}

export default withRouter(PreviewDataset)
